import axios from 'axios';
import { useUiStore } from '../store/ui.js';
import router from '../router.js';

let currentCsrf = () => document.querySelector('meta[name="csrf-token"]')?.content || '';

const api = axios.create({
    baseURL: '/api',
    headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
    withCredentials: true,
});

api.interceptors.request.use((cfg) => {
    if (!['get', 'head', 'options'].includes((cfg.method || 'get').toLowerCase())) {
        cfg.headers['X-CSRF-Token'] = currentCsrf();
    }
    return cfg;
});

api.interceptors.response.use(
    (r) => {
        // Refresh the CSRF meta from /api/me responses (token rotates after login)
        if (r.config?.url === '/me' && r.data?.csrf_token) {
            const meta = document.querySelector('meta[name="csrf-token"]');
            if (meta) meta.content = r.data.csrf_token;
        }
        return r;
    },
    (err) => {
        const status = err.response?.status;
        const data = err.response?.data || {};
        // Get the toast store lazily — store may not be ready when api.js loads
        let ui = null;
        try { ui = useUiStore(); } catch { /* not ready yet */ }

        if (status === 401) {
            // Stille Aufrufe prüfen absichtlich, OB eine Sitzung besteht
            // (der Start ruft /me auf) — die dürfen nicht umleiten,
            // sonst landet jeder Erstaufruf auf der Anmeldemaske, bevor
            // die Oberfläche überhaupt entscheiden konnte.
            if (err.config?._silent) return Promise.reject(err);
            // Routerwechsel statt window.location: ein harter Ladevorgang
            // würde die Einzelseiten-Anwendung samt Zustand wegwerfen.
            if (router.currentRoute.value.name !== 'login') {
                router.push({ name: 'login', query: { next: router.currentRoute.value.fullPath } });
            }
            return Promise.reject(err);
        }
        if (status === 419) {
            // CSRF expired — refresh and ask user to retry
            ui?.warn('Sitzung abgelaufen. Bitte Seite neu laden.', { sticky: true });
            return Promise.reject(err);
        }
        if (err.config?._silent) return Promise.reject(err);

        // 422 has two response shapes in this codebase:
        //   { errors: { field: msg, … } } — field-level validation;
        //     forms render these inline themselves, no toast.
        //   { error:  "single message" }  — general "request can't be
        //     processed" (e.g. "PDF enthält keine Textebene"); the
        //     operator needs to see this — toast it like any other
        //     error status.
        // Previously we blindly skipped ALL 422s, which silently
        // swallowed the general-error variant.
        if (status === 422 && data.errors && !data.error) {
            return Promise.reject(err);
        }

        // Surface a meaningful error message
        const msg = data.error || (data.errors && Object.values(data.errors).join(', ')) || err.message || 'Unbekannter Fehler';
        ui?.error(msg);
        return Promise.reject(err);
    },
);

export default api;
